<script setup>
import StatusPanel from '../../panels/Status';
import PlatformPanel from '../../panels/Platform.vue';
</script>

<template>
  <table-base
    has-phone-layout
    :on-add="onAdd"
    :add-label="addLabel"
    :columns="columns"
    :items="items"
    :pages="pages"
    :pagination="pagination"
    :is-loading="isLoading"
    has-custom-items-template
    :on-select-item="onSelectItem"
    :on-update-pagination="onUpdatePagination"
    :on-change-page="onChangePageProxy"
    data-cy="event-promotions-table"
  >
    <template v-slot:item-cell="scope">
      <template v-if="scope.cell.column.value === 'title'">
        <span class="d-flex flex-column gap-4">
          <span>{{ scope.cell.item.title }}</span>
          <span class="text-caption font-weight-bold text-grey">{{ scope.cell.item.type.name }}</span>
        </span>
      </template>
      <template v-else-if="scope.cell.column.value === 'dates'">
        {{ formatDates(scope.cell.item.start_date, scope.cell.item.end_date) }}
      </template>
      <template v-else-if="scope.cell.column.value === 'status'">
        <StatusPanel
          table-group="eventsPromotions"
          :status="scope.cell.item.status.name"
        />
      </template>
      <template v-else-if="scope.cell.column.value === 'sponsorships'">
        <div
          class="event-promotions-table__sponsorships"
          v-for="(sponsorship, index) in scope.cell.item.sponsorships"
          :key="index"
        >
          <span class="event-promotions-table__sponsorships-placement">
            {{ sponsorship.placements.map(placement => placement.name).join(', ') }}
          </span>
          <div :class="{
              'event-promotions-table__sponsorships-merchants': true,
              'flex-wrap': expandedRows[findIndexById(scope.cell.item.id)].includes(index),
            }">
            <v-chip
              v-for="(merchant, merchantIndex) in sponsorship.merchants"
              v-show="merchantIndex < 2 || expandedRows[findIndexById(scope.cell.item.id)].includes(index)"
              base-color="#a6a6a6"
              :key="merchant?.name"
            >
              <span class="text-black">{{ merchant?.name }}</span>
            </v-chip>
            <v-icon
              v-if="scope.cell.item.sponsorships[index].merchants?.length > 2"
              @click="toggleExpandSponsorships($event, findIndexById(scope.cell.item.id), index)"
            >
              {{ expandedRows[findIndexById(scope.cell.item.id)].includes(index) ? 'expand_less' : 'expand_more' }}
            </v-icon>
          </div>
        </div>
      </template>
      <template v-else-if="scope.cell.column.value === 'country'">
        <PlatformPanel :country="scope.cell.item.country" />
      </template>
      <template v-else>
        {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
      </template>
    </template>
  </table-base>
</template>

<script>
import columns from '../../../config/events/promotionsColumns';
import { extractNestedProp } from '@/utils';

export default {
  props: {
    onAdd: {
      type: Function,
      default: null,
    },
    addLabel: {
      type: String,
      default: undefined,
    },
    items: {
      type: Array,
      required: true,
    },
    pages: {
      type: Number,
      required: true,
    },
    pagination: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    onSelectItem: {
      type: Function,
      required: true,
    },
    onUpdatePagination: {
      type: Function,
      required: true,
    },
    onChangePage: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      expandedRows: Array.from({ length: 100 }, () => []),
      columns,
    };
  },
  methods: {
    onChangePageProxy(page) {
      this.onChangePage(page);
      this.expandedRows = Array.from({ length: 100 }, () => []);
    },
    findIndexById(id) {
      return this.items.findIndex(item => item.id === id);
    },
    toggleExpandSponsorships(e, row, index) {
      e.stopPropagation();

      const itemIndex = this.expandedRows[row].findIndex(i => i === index);

      if (itemIndex > -1) {
        this.expandedRows[row].splice(itemIndex, 1);
      } else {
        this.expandedRows[row].push(index);
      }

    },
    displayColumnValue(item, value) {
      return extractNestedProp(item, value);
    },
    formatDates(startDate, endDate) {
      const start = this.$dayjs(startDate).format('MMM D, YYYY');
      const end = this.$dayjs(endDate).format('MMM D, YYYY');
      return `${start} - ${end}`;
    },
  },
};
</script>

<style scoped lang="scss">
@use '../../../assets/styles/theme' as *;

.v-datatable {
  min-width: $table-min-width;
}

.event-promotions-table {
  &__sponsorships {
    display: flex;
    flex-direction: column;
    margin-bottom: 6px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__sponsorships-placement {
    font-weight: 600;
    font-size: 12px;
  }

  &__sponsorships-merchants {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;

    // A phone card is too narrow for two merchants side by side.
    @include phone {
      flex-wrap: wrap;
    }
  }
}
</style>
